import axios, { AxiosResponse } from 'axios';
import mime from 'mime';

import { mimeTypes } from 'app/constants/mime-types';

// Exports
export const base64ToArrayBuffer = (base64: string) => {
  const binaryString = window.atob(base64);
  const binaryLen = binaryString.length;
  const bytes = new Uint8Array(binaryLen);
  for (let i = 0; i < binaryLen; i += 1) {
    const ascii = binaryString.charCodeAt(i);
    bytes[i] = ascii;
  }

  return bytes;
};

export const removeExtensionFromFileName = (fileName: string) => {
  const fileExtensionIndex = fileName.lastIndexOf('.');

  if (fileExtensionIndex === -1) {
    return fileName;
  }

  return fileName.substring(0, fileExtensionIndex);
};

const createFileExtension = (contentType: string) => {
  const fileExtension = mime.getExtension(contentType);

  return fileExtension ? `.${fileExtension}` : '';
};

const saveByteArray = (fileName: string, byte: Uint8Array, contentType: string) => {
  const parsedFileName = removeExtensionFromFileName(fileName);
  const blob = new Blob([byte] as BlobPart[], { type: contentType });
  if (navigator.msSaveBlob) {
    return navigator.msSaveBlob(blob, parsedFileName);
  }
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = `${parsedFileName}${createFileExtension(contentType)}`;
  link.click();
  link.id = parsedFileName;
  document.getElementById(parsedFileName)?.remove();
};

function convertUint8ToBase64(u8Arr: Uint8Array) {
  const chunkSize = 0x8000; // arbitrary number
  let index = 0;
  const length = u8Arr.length;
  let result = '';
  let slice: null | Uint8Array = null;
  while (index < length) {
    slice = u8Arr.subarray(index, Math.min(index + chunkSize, length));
    result += String.fromCharCode.apply(null, slice) as string;
    index += chunkSize;
  }

  return btoa(result);
}

function readFileAsync(file: File) {
  return new Promise<ArrayBuffer | null | string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result);
    };

    reader.onerror = reject;

    reader.readAsArrayBuffer(file);
  });
}

export async function convertFileToBase64(file: File) {
  const readFile = await readFileAsync(file);
  if (readFile && typeof readFile !== 'string') {
    const uint8Array = new Uint8Array(readFile);

    return convertUint8ToBase64(uint8Array);
  }

  return undefined;
}

export function getFileExtension(fileType: string): string {
  const splitType = fileType.split('/');

  return splitType[splitType.length - 1] ?? '';
}

export const downloadUrl = (fileName: string, url: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.target = '_blank';
  link.click();
};

export function retry<T>(fn: () => Promise<T>, retriesLeft = 2, interval = 500) {
  return new Promise<T>((resolve, reject) => {
    fn()
      .then(resolve)
      .catch(() => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            // do not throw an error here. We are using this for code-splitting
            // it is possible the user gets here when the service worker updates
            window.location.reload();
          }

          // Passing on "reject" is the important part
          retry(fn, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });
}

export const getFileNameFromContentDisposition = (headers: AxiosResponse['headers']) => {
  let filename: null | string = null;
  const contentDispositionHeader = headers['content-disposition'] as string | undefined;
  if (contentDispositionHeader?.includes('attachment')) {
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(contentDispositionHeader ?? '');
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    filename = matches?.[1]?.replace(/['"]/g, '') || null;
  }

  return filename;
};

export const downloadFile =
  async (cb: () => Promise<AxiosResponse<Blob>>,
    fallbackFileName: string) => {
    const response = await cb();
    // If the generated file is too large, Core will upload to S3 and send a pre-signed url
    if (response?.data?.type === mimeTypes.text) {
      const parsedS3Url = await response.data.text();
      const s3response = await axios.get<Uint8Array>(parsedS3Url);
      saveByteArray(
        getFileNameFromContentDisposition(s3response.headers) ?? fallbackFileName,
        s3response.data,
        s3response.headers['content-type'] as string ?? '',
      );
    } else { // otherwise, Core will send a file
      saveByteArray(
        getFileNameFromContentDisposition(response.headers) ?? fallbackFileName,
        response.data as unknown as Uint8Array,
        response.headers['content-type'] as string ?? '',
      );
    }
  };
